import { useMutation, useQuery } from '@tanstack/react-query';
import {
  type AssessmentResultFilter,
  type Assessment_AssessmentResultQuery,
  CommentType,
  EmulateHeaders,
  type SaveAssessmentResultInput,
  type UseQueryReturnType,
  gqlClient,
  graphql,
  queryClient,
} from '@tyro/api';
import { toast } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { useCallback } from 'react';
import { assessmentsKeys } from './keys';

const assessmentResults = graphql(/* GraphQL */ `
  query assessment_assessmentResult($filter: AssessmentResultFilter) {
    assessment_assessmentResult(filter: $filter) {
      id
      assessmentId
      studentPartyId
      student {
        person {
          partyId
          title {
            id
            name
            nameTextId
          }
          firstName
          lastName
          avatarUrl
          type
        }
        extensions {
          priority
          aen
          medical
        }
      }
      studentClassGroup
      studentProgramme {
        shortName
      }
      subjectGroup {
        partyId
        name
        irePP {
          examinable
        }
      }
      studentStudyLevel
      result
      gradeId
      gradeResult
      targetResult
      targetGradeId
      targetGradeResult
      teacherComment {
        id
        assessmentId
        studentPartyId
        comment
        commentBankCommentId
        commenterUserType
        commenterPartyId
        subjectGroupPartyId
      }
      extraFields {
        id
        extraFieldType
        assessmentResultId
        assessmentExtraFieldId
        result
        gradeSetGradeId
        gradeNameTextId
        commentBankCommentId
      }
      examinable
      absent
      ppodPublished
      ppodErrorMessage
      ppodResult
    }
  }
`);

const updateAssessmentResult = graphql(/* GraphQL */ `
  mutation assessment_saveAssessmentResults(
    $input: [SaveAssessmentResultInput!]
  ) {
    assessment_saveAssessmentResults(input: $input) {
      id
    }
  }
`);

const assessmentResultsQuery = (
  academicNamespaceId: number,
  filter: AssessmentResultFilter,
) => ({
  queryKey: assessmentsKeys.resultsBySubjectGroup(
    academicNamespaceId,
    filter ?? {},
  ),
  queryFn: () =>
    gqlClient.request(
      assessmentResults,
      { filter },
      {
        [EmulateHeaders.ACADEMIC_NAMESPACE_ID]: academicNamespaceId.toString(),
      },
    ),
});

export function getAssessmentResults(
  academicNamespaceId: number,
  filter: AssessmentResultFilter,
) {
  return queryClient.fetchQuery(
    assessmentResultsQuery(academicNamespaceId, filter),
  );
}

export function useAssessmentResults(
  academicNamespaceId: number,
  filter: AssessmentResultFilter | null,
) {
  return useQuery({
    ...assessmentResultsQuery(academicNamespaceId, filter ?? {}),
    enabled: !!filter,
    select: useCallback(
      ({ assessment_assessmentResult }: Assessment_AssessmentResultQuery) =>
        assessment_assessmentResult?.map((result) => {
          const extraFields =
            result?.extraFields?.reduce(
              (acc, extraField) => {
                acc[extraField.assessmentExtraFieldId] = extraField;
                return acc;
              },
              {} as Record<
                number,
                NonNullable<(typeof result)['extraFields']>[number]
              >,
            ) ?? {};

          return {
            ...result,
            teacherCommentType: result.teacherComment?.commentBankCommentId
              ? CommentType.CommentBank
              : CommentType.FreeForm,
            extraFields,
          };
        }),
      [],
    ),
  });
}

export type ReturnTypeFromUseAssessmentResults = UseQueryReturnType<
  typeof useAssessmentResults
>[number];

export function useUpdateAssessmentResult(
  academicNamespaceId: number,
  assessmentFilter: AssessmentResultFilter,
) {
  const { t } = useTranslation(['common']);

  return useMutation({
    mutationFn: (input: SaveAssessmentResultInput[]) =>
      gqlClient.request(
        updateAssessmentResult,
        { input },
        {
          [EmulateHeaders.ACADEMIC_NAMESPACE_ID]:
            academicNamespaceId.toString(),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: assessmentsKeys.resultsBySubjectGroup(academicNamespaceId, {
          assessmentId: assessmentFilter.assessmentId,
        }),
      });
    },
    onError: () => {
      toast.error(t('common:snackbarMessages.errorFailed'));
    },
  });
}
